// The static view of a body (drawn until the live copy has synced, and for
// bodies that cannot be written) dressed like the editor's node views, so
// nothing moves when the editor takes over:
// - files: the same image box and button, chips and video boxes;
// - footnotes: the same numbers (the editor's come from decorations, which a
//   static view does not have) and buttons;
// - code blocks: the same band with the language and the copy button;
// - the parts' blocks (block-parts.ts): a ```ui block gets its editor view's
//   box with the UI shown but not usable.
// Pressing does what it does in the editor: an image opens the viewer, a
// footnote reference scrolls to its definition, the band copies the code.
//
// What still differs: tables have no controls, callouts no color bar,
// toggles no button (each has the same box), and a mermaid diagram shows its
// source until the editor draws it.

import { normalizeFileWidth } from "@trameria/editor";
import { m } from "@trameria/i18n";
import {
  type MouseEvent as ReactMouseEvent,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useMeApp } from "../../app/context";
import { type DocumentBlockPart, useDocumentBlockParts } from "./block-parts";
import { copyCodeBlock, wrapCodeBlocks } from "./code-copy";
import {
  FILE_CHIP_CLASS,
  FILE_CHIP_ICON_CLASS,
  FILE_CHIP_ICON_SVG,
  FILE_IMAGE_BOX_CLASS,
  FILE_IMAGE_CLASS,
  FILE_IMAGE_OPEN_CLASS,
  FILE_IMAGE_RETRY_MS,
  fileImageStyle,
} from "./file-view";
import {
  openFileLightbox,
  VIDEO_BOX_CLASS,
  VideoPlayer,
  videoOuterStyle,
} from "./media";
import { fileUrl } from "./upload";

/** A chip, built like `FileChip` (file-view.tsx): the same classes and icon markup. */
function fileChip(doc: Document, href: string, name: string): HTMLElement {
  const link = doc.createElement("a");
  link.href = href;
  link.className = FILE_CHIP_CLASS;
  const icon = doc.createElement("span");
  icon.className = FILE_CHIP_ICON_CLASS;
  icon.innerHTML = FILE_CHIP_ICON_SVG;
  const label = doc.createElement("span");
  label.className = "truncate";
  label.textContent = name;
  link.replaceChildren(icon, label);
  return link;
}

/**
 * An image in its box and button, as `FileView` draws it; the width
 * (`data-file-width`) goes through the same `fileImageStyle`, or the width
 * would change at the switch. No grip and no "…" (nothing to edit here), but
 * the same box.
 */
function fileImage(
  doc: Document,
  src: string,
  name: string,
  width: number | null,
): HTMLElement {
  const box = doc.createElement("div");
  box.className = FILE_IMAGE_BOX_CLASS;
  const open = doc.createElement("button");
  open.type = "button";
  open.tabIndex = -1;
  open.setAttribute("data-file-open", "");
  open.setAttribute("aria-label", m.blocks_file_enlarge_named({ name }));
  open.className = FILE_IMAGE_OPEN_CLASS;
  const img = doc.createElement("img");
  img.src = src;
  img.alt = name;
  img.className = FILE_IMAGE_CLASS;
  const style = fileImageStyle(width);
  if (style) {
    img.style.width = style.width;
    img.style.maxHeight = style.maxHeight;
  }
  open.replaceChildren(img);
  box.replaceChildren(open);
  return box;
}

/**
 * A video's place: the player is a React component, so only its box is made
 * here and `StaticBodyHtml` portals the same `VideoPlayer` into it. The chip
 * inside is the fallback until then (and for anything that reads the HTML
 * without React); it is removed before the player goes in.
 */
function videoHost(
  doc: Document,
  apiBase: string,
  fileId: string,
  name: string,
  width: number | null,
): HTMLElement {
  const host = doc.createElement("div");
  host.className = VIDEO_BOX_CLASS;
  host.setAttribute("data-video-host", fileId);
  host.setAttribute("data-file-name", name);
  if (width !== null) host.setAttribute("data-file-width", String(width));
  host.style.width = videoOuterStyle(width).width;
  host.replaceChildren(
    fileChip(doc, fileUrl(apiBase, fileId, { download: true }), name),
  );
  return host;
}

function dressFiles(root: HTMLElement, apiBase: string): void {
  const doc = root.ownerDocument;
  for (const div of root.querySelectorAll<HTMLElement>(
    "div[data-trameria-file]",
  )) {
    const fileId = div.getAttribute("data-trameria-file") ?? "";
    const name = div.getAttribute("data-file-name") ?? "";
    const width = normalizeFileWidth(div.getAttribute("data-file-width"));
    const image = div.getAttribute("data-file-image") === "true";
    if (image && div.getAttribute("data-file-video") === "true")
      div.replaceChildren(videoHost(doc, apiBase, fileId, name, width));
    else if (image)
      div.replaceChildren(
        fileImage(doc, fileUrl(apiBase, fileId), name, width),
      );
    // Chips have no width (a `data-file-width` is ignored).
    else
      div.replaceChildren(
        fileChip(doc, fileUrl(apiBase, fileId, { download: true }), name),
      );
  }
  // Files inside a paragraph are always chips, even an image written
  // mid-line (as `FileInlineView`): a tall image would stretch the line.
  for (const span of root.querySelectorAll<HTMLElement>(
    "span[data-trameria-file-inline]",
  )) {
    const fileId = span.getAttribute("data-trameria-file-inline") ?? "";
    span.replaceChildren(
      fileChip(
        doc,
        fileUrl(apiBase, fileId, { download: true }),
        span.getAttribute("data-file-name") ?? "",
      ),
    );
  }
}

/**
 * Numbers the footnotes as the editor does (`footnoteOrder`: the order of the
 * definitions at the end, from 1). Without this the switch to the editor would
 * turn `[^abc]` into `1`, and the definition's mark `[^1]` -> `1.` would move
 * its text by the difference in width.
 */
function numberFootnotes(root: HTMLElement): void {
  const defs = [
    ...root.querySelectorAll<HTMLElement>("div[data-footnote-def]"),
  ];
  const refs = [
    ...root.querySelectorAll<HTMLElement>("sup[data-footnote-ref]"),
  ];
  if (defs.length === 0 && refs.length === 0) return;
  const order = new Map<string, number>();
  for (const def of defs) {
    const label = def.getAttribute("data-footnote-def") ?? "";
    if (!order.has(label)) order.set(label, order.size + 1);
  }
  for (const def of defs) {
    const n = order.get(def.getAttribute("data-footnote-def") ?? "");
    const mark = def.querySelector(".trameria-footnote-label");
    if (n === undefined || !mark) continue;
    mark.textContent = `${n}.`;
    def.setAttribute("data-n", String(n));
  }
  for (const ref of refs) {
    const n = order.get(ref.getAttribute("data-label") ?? "");
    if (n === undefined) {
      // Nowhere to go: stays text, not a button.
      ref.setAttribute("data-missing", "true");
      continue;
    }
    ref.setAttribute("data-n", String(n));
    // A button (it scrolls to the definition): reachable from the keyboard,
    // and the same shape as the editor's (sup > button).
    const button = ref.ownerDocument.createElement("button");
    button.type = "button";
    button.setAttribute("aria-label", m.blocks_footnote_to_def({ n }));
    button.textContent = String(n);
    ref.replaceChildren(button);
  }
}

/**
 * What the caller of a static body adds to its HTML (detached, after
 * everything else): e.g. a conversation drawing names it knows as images.
 * Keep the same function while its result would not change; a new one draws
 * the body again.
 */
export type StaticBodyDress = (root: HTMLElement) => void;

/**
 * The editor's HTML of a body, dressed as described at the top; then each
 * part's own blocks (block-parts.ts: a ```ui block gets the box its editor
 * view has), then the caller's `dress`.
 */
export function dressStaticHtml(
  html: string,
  apiBase: string,
  parts: readonly DocumentBlockPart[] = [],
  dress?: StaticBodyDress,
): string {
  const root = document.createElement("div");
  root.innerHTML = html;
  dressFiles(root, apiBase);
  numberFootnotes(root);
  wrapCodeBlocks(root);
  for (const part of parts) part.staticBody?.dress(root);
  dress?.(root);
  return root.innerHTML;
}

/** Scrolls from a footnote reference to its definition (labels compared, not put in a selector). */
function scrollToFootnoteDef(root: HTMLElement, target: Element): boolean {
  const ref = target.closest("sup[data-footnote-ref]");
  if (!ref || !root.contains(ref)) return false;
  const label = ref.getAttribute("data-label") ?? "";
  for (const def of root.querySelectorAll<HTMLElement>(
    "div[data-footnote-def]",
  )) {
    if (def.getAttribute("data-footnote-def") !== label) continue;
    def.scrollIntoView?.({ block: "center" });
    return true;
  }
  return false;
}

/** Opens the viewer from an image's button, as `FileView` does. */
function openImage(root: HTMLElement, target: Element): boolean {
  const open = target.closest<HTMLElement>("button[data-file-open]");
  if (!open || !root.contains(open)) return false;
  const img = open.querySelector("img");
  if (!img) return false;
  // The relative URL, as the node view passes it (`img.src` is absolute).
  const src = img.getAttribute("src") ?? "";
  openFileLightbox(open, {
    src,
    name: img.getAttribute("alt") ?? "",
    download: src ? `${src}?dl=1` : undefined,
  });
  return true;
}

/** Marks an image already retried (the static view has no React state per image). */
const RETRIED_ATTR = "data-file-retried";

interface VideoHost {
  el: HTMLElement;
  src: string;
  download: string;
  name: string;
  width: number | null;
}

/**
 * Renders dressed HTML with the editor's behaviours: pressing an image, a
 * footnote reference or a copy button (one delegated listener each, not one
 * per element), retrying an image once, and the video players.
 */
export function StaticBodyHtml({
  html,
  className,
  markdown = "",
  dress,
}: {
  html: string;
  className: string;
  /** The markdown the HTML was made from (for the parts' slots). */
  markdown?: string;
  /** What the caller adds to the HTML (`StaticBodyDress`). */
  dress?: StaticBodyDress;
}) {
  const { apiBase } = useMeApp();
  const parts = useDocumentBlockParts();
  const dressed = useMemo(
    () => dressStaticHtml(html, apiBase, parts, dress),
    [html, apiBase, parts, dress],
  );
  // The wrapper is made again only when the HTML changes. Parts portal into
  // elements of this HTML (a ui block's iframe); setting the same HTML again
  // would drop them, and reload the iframes.
  const inner = useMemo(() => ({ __html: dressed }), [dressed]);
  const root = useRef<HTMLDivElement>(null);
  const [videos, setVideos] = useState<VideoHost[]>([]);
  const timers = useRef(new Set<ReturnType<typeof setTimeout>>());

  // frontend-development: not a fetch; finds the video boxes of the HTML just drawn to portal players into.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `dressed` is the HTML the boxes come from; new HTML means new boxes
  useLayoutEffect(() => {
    const el = root.current;
    if (!el) return;
    const found = [
      ...el.querySelectorAll<HTMLElement>("[data-video-host]"),
    ].map((host) => {
      const fileId = host.getAttribute("data-video-host") ?? "";
      // The fallback chip leaves before the player comes in.
      host.replaceChildren();
      return {
        el: host,
        src: fileUrl(apiBase, fileId),
        download: fileUrl(apiBase, fileId, { download: true }),
        name: host.getAttribute("data-file-name") ?? "",
        width: normalizeFileWidth(host.getAttribute("data-file-width")),
      };
    });
    setVideos(found);
  }, [dressed, apiBase]);

  /**
   * A failed image: retried once after a while, then a chip (as `FileView`).
   * Right after a paste the file has no holder yet and others get 404 for a
   * few seconds - exactly the static view's window. `error` does not bubble
   * and React does not see elements of injected HTML, so a native capturing
   * listener takes it.
   */
  // frontend-development: not a fetch; retries failed images of the injected HTML.
  useEffect(() => {
    const el = root.current;
    const pending = timers.current;
    if (!el) return;
    const onError = (e: Event) => {
      const img = e.target;
      if (!(img instanceof HTMLImageElement)) return;
      const div = img.closest<HTMLElement>("div[data-trameria-file]");
      if (!div) return;
      if (img.hasAttribute(RETRIED_ATTR)) {
        const fileId = div.getAttribute("data-trameria-file") ?? "";
        div.replaceChildren(
          fileChip(
            img.ownerDocument,
            fileUrl(apiBase, fileId, { download: true }),
            div.getAttribute("data-file-name") ?? "",
          ),
        );
        return;
      }
      const t = setTimeout(() => {
        pending.delete(t);
        // A new element loads again (assigning the same src does not).
        const next = img.cloneNode(true) as HTMLImageElement;
        next.setAttribute(RETRIED_ATTR, "");
        img.replaceWith(next);
      }, FILE_IMAGE_RETRY_MS);
      pending.add(t);
    };
    el.addEventListener("error", onError, true);
    return () => {
      el.removeEventListener("error", onError, true);
      for (const t of pending) clearTimeout(t);
      pending.clear();
    };
  }, [apiBase]);

  const onClick = (e: ReactMouseEvent<HTMLDivElement>) => {
    const el = root.current;
    const target = e.target;
    if (!el || !(target instanceof Element)) return;
    if (openImage(el, target) || scrollToFootnoteDef(el, target)) return;
    if (parts.some((p) => p.staticBody?.click?.(el, target) === true)) return;
    copyCodeBlock(el, target);
  };

  return (
    <>
      {/* biome-ignore lint/a11y/useKeyWithClickEvents lint/a11y/noStaticElementInteractions: a delegation point; what is pressed are the buttons inside (images, footnote references, copy), reachable by keyboard themselves */}
      <div
        ref={root}
        className={className}
        // Its images step together in the viewer (gallery.ts).
        data-media-gallery=""
        onClick={onClick}
        // The HTML comes from the editor's schema serializer and the dressing
        // above, never from the input text (see renderMarkdownToHtml).
        // biome-ignore lint/security/noDangerouslySetInnerHtml: schema-serialized HTML
        dangerouslySetInnerHTML={inner}
      />
      {/* After the box: their layout effects run once its ref is set. */}
      {parts.map((part) => {
        const Slots = part.staticBody?.Slots;
        return Slots ? (
          <Slots key={part.id} root={root} html={dressed} markdown={markdown} />
        ) : null;
      })}
      {videos.map((v) =>
        createPortal(
          <VideoPlayer
            src={v.src}
            name={v.name}
            download={v.download}
            width={v.width}
          />,
          v.el,
          v.src,
        ),
      )}
    </>
  );
}
